import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ClientLayout, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import RichTextEditor from '../components/RichTextEditor';
import { 
  ChevronLeft, 
  ChevronRight, 
  Save, 
  CheckCircle2, 
  Circle,
  Loader2,
  FileCheck,
  UserCog,
  Building2,
  Upload,
  FileText,
  Sparkles,
  PanelRightClose,
  PanelRightOpen,
  X,
  Layers,
  ArrowRight,
  Paperclip,
  Trash2,
  ExternalLink,
  HelpCircle
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TemplateForm() {
  const { templateId, submissionId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [template, setTemplate] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [formData, setFormData] = useState({});
  const [vendorFields, setVendorFields] = useState([]);
  const [fieldAssignments, setFieldAssignments] = useState({});
  const [userDepartments, setUserDepartments] = useState([]);
  const [currentSection, setCurrentSection] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showDocPanel, setShowDocPanel] = useState(false);
  const [importedDocText, setImportedDocText] = useState('');
  const [checklistView, setChecklistView] = useState(false);
  const [evidenceFiles, setEvidenceFiles] = useState({});
  const [uploadingEvidence, setUploadingEvidence] = useState(null);
  const [auditorToken, setAuditorToken] = useState(null);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [crossImportSources, setCrossImportSources] = useState(null);
  const [crossImportLoading, setCrossImportLoading] = useState(false);
  const [showCrossImport, setShowCrossImport] = useState(false);
  
  const saveTimeoutRef = useRef(null);
  const importFileRef = useRef(null);
  const isNewSubmission = !!templateId;

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, submissionId]);

  const loadData = async () => {
    try {
      // Load user departments
      const meRes = await axios.get(`${API_URL}/auth/me`);
      setUserDepartments(meRes.data.departments || []);

      if (isNewSubmission) {
        const templateRes = await axios.get(`${API_URL}/templates/${templateId}`);
        setTemplate(templateRes.data);
        
        const submissionRes = await axios.post(`${API_URL}/submissions`, { template_id: templateId });
        setSubmission(submissionRes.data);
        setFormData(submissionRes.data.data || {});
        setVendorFields(submissionRes.data.vendor_fields || []);
        setFieldAssignments(submissionRes.data.field_assignments || {});
        setCurrentSection(0);
        
        navigate(`/submissions/${submissionRes.data.id}`, { replace: true });
      } else {
        const submissionRes = await axios.get(`${API_URL}/submissions/${submissionId}`);
        setSubmission(submissionRes.data);
        setFormData(submissionRes.data.data || {});
        setVendorFields(submissionRes.data.vendor_fields || []);
        setFieldAssignments(submissionRes.data.field_assignments || {});
        setCurrentSection(submissionRes.data.current_section || 0);
        
        const templateRes = await axios.get(`${API_URL}/templates/${submissionRes.data.template_id}`);
        setTemplate(templateRes.data);

        // Load evidence files for SOC 2 templates
        if (templateRes.data.soc2_selectable_criteria && submissionRes.data.id) {
          try {
            const evRes = await axios.get(`${API_URL}/submissions/${submissionRes.data.id}/evidence`);
            setEvidenceFiles(evRes.data.evidence || {});
          } catch {}
        }
        setAuditorToken(submissionRes.data.auditor_token || null);

        // Jump to section from URL param (e.g. ?section=3 from "Fix Me" button)
        const sectionParam = searchParams.get('section');
        if (sectionParam !== null) {
          const idx = parseInt(sectionParam, 10);
          if (!isNaN(idx) && idx >= 0) {
            setCurrentSection(idx);
          }
          // Clear the param so it doesn't persist on reload
          setSearchParams({}, { replace: true });
        }

        // Auto-suggest cross-import if the form is mostly empty
        const data = submissionRes.data.data || {};
        const filledCount = Object.values(data).filter(v => v && String(v).trim()).length;
        if (filledCount < 5) {
          try {
            const srcRes = await axios.get(`${API_URL}/submissions/${submissionRes.data.id}/import-from-submissions`);
            if (srcRes.data.sources?.length > 0) {
              setCrossImportSources(srcRes.data.sources);
              setShowCrossImport(true);
            }
          } catch { /* ignore */ }
        }
      }
    } catch (error) {
      toast.error('Failed to load form data');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const saveProgress = useCallback(async (data, section, vFields, completed = false) => {
    if (!submission) return;
    
    setSaving(true);
    try {
      await axios.put(`${API_URL}/submissions/${submission.id}`, {
        data,
        current_section: section,
        is_completed: completed,
        vendor_fields: vFields,
        field_assignments: fieldAssignments
      });
      setLastSaved(new Date());
    } catch (error) {
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  }, [submission, fieldAssignments]);

  // Auto-save when form data changes
  useEffect(() => {
    if (!submission || loading) return;
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      saveProgress(formData, currentSection, vendorFields, false);
    }, 2000);
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [formData, currentSection, vendorFields, fieldAssignments, submission, loading, saveProgress]);

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handlePrefillFromProfile = async () => {
    if (!submission) return;
    setPrefillLoading(true);
    try {
      const res = await axios.get(`${API_URL}/submissions/${submission.id}/prefill`);
      const { fields, count } = res.data;
      if (count === 0) {
        toast.info('No matching fields found in your profile. Update your profile with more information.');
        return;
      }
      // Only fill empty fields
      let filled = 0;
      setFormData(prev => {
        const updated = { ...prev };
        for (const [key, value] of Object.entries(fields)) {
          if (!updated[key] || updated[key].trim() === '') {
            updated[key] = value;
            filled++;
          }
        }
        return updated;
      });
      toast.success(`Pre-filled ${filled} field${filled !== 1 ? 's' : ''} from your profile`);
    } catch (error) {
      toast.error('Failed to load profile data');
    } finally {
      setPrefillLoading(false);
    }
  };

  const loadCrossImportSources = async () => {
    if (!submission) return;
    setCrossImportLoading(true);
    try {
      const res = await axios.get(`${API_URL}/submissions/${submission.id}/import-from-submissions`);
      setCrossImportSources(res.data.sources);
      setShowCrossImport(true);
      if (!res.data.sources.length) toast.info('No other submissions with overlapping fields found');
    } catch { toast.error('Failed to check for importable data'); }
    finally { setCrossImportLoading(false); }
  };

  const handleCrossImport = async (sourceId) => {
    if (!submission) return;
    setCrossImportLoading(true);
    try {
      const res = await axios.post(`${API_URL}/submissions/${submission.id}/import-from-submission/${sourceId}`);
      toast.success(res.data.message);
      // Reload submission data
      const subRes = await axios.get(`${API_URL}/submissions/${submission.id}`);
      setFormData(subRes.data.data || {});
      setShowCrossImport(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setCrossImportLoading(false); }
  };

  const handleFieldAssignment = (fieldName, assignee) => {
    // assignee: "" (self), "__vendor__" (vendor), or department name
    setFieldAssignments(prev => {
      const updated = { ...prev };
      if (!assignee) {
        delete updated[fieldName];
      } else {
        updated[fieldName] = assignee;
      }
      return updated;
    });
    // Keep vendorFields in sync
    if (assignee === '__vendor__') {
      setVendorFields(prev => prev.includes(fieldName) ? prev : [...prev, fieldName]);
    } else {
      setVendorFields(prev => prev.filter(f => f !== fieldName));
    }
  };

  // Evidence handlers
  const handleEvidenceUpload = async (fieldName, file) => {
    if (!submission) return;
    setUploadingEvidence(fieldName);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/submissions/${submission.id}/evidence/${fieldName}`, fd);
      setEvidenceFiles(prev => ({
        ...prev,
        [fieldName]: [...(prev[fieldName] || []), res.data.file]
      }));
      toast.success(`Evidence uploaded: ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingEvidence(null);
    }
  };

  const handleEvidenceDelete = async (fieldName, fileId) => {
    if (!submission) return;
    try {
      await axios.delete(`${API_URL}/submissions/${submission.id}/evidence/${fieldName}/${fileId}`);
      setEvidenceFiles(prev => ({
        ...prev,
        [fieldName]: (prev[fieldName] || []).filter(f => f.id !== fileId)
      }));
      toast.success('Evidence removed');
    } catch {
      toast.error('Failed to delete evidence');
    }
  };

  const handleGenerateAuditorLink = async () => {
    if (!submission) return;
    try {
      const res = await axios.post(`${API_URL}/submissions/${submission.id}/auditor-link`);
      setAuditorToken(res.data.auditor_token);
      const link = `${window.location.origin}/auditor/${res.data.auditor_token}`;
      await navigator.clipboard.writeText(link);
      toast.success('Auditor link copied to clipboard!');
    } catch {
      toast.error('Failed to generate auditor link');
    }
  };


  const handleNextSection = () => {
    if (currentSection < activeSections.length - 1) {
      setCurrentSection(currentSection + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleImport = async (mode) => {
    const file = importFileRef.current?.files?.[0];
    if (!file) { toast.error('Please select a file'); return; }
    
    const allowed = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) {
      toast.error('Only PDF and Word (.docx) files are supported'); return;
    }
    
    setImportLoading(true);
    setImportResult(null);
    
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('mode', mode);
      
      const res = await axios.post(`${API_URL}/submissions/${submission.id}/import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      
      if (mode === 'ai') {
        setImportResult(res.data);
        // Reload submission data with AI-mapped fields
        const subRes = await axios.get(`${API_URL}/submissions/${submission.id}`);
        setFormData(subRes.data.data || {});
        toast.success(`AI imported ${res.data.fields_mapped} fields successfully!`);
        setShowImportModal(false);
      } else {
        // Manual mode - show document panel
        setImportedDocText(res.data.document_text);
        setShowDocPanel(true);
        setShowImportModal(false);
        toast.success('Document loaded! Use the side panel to copy content into fields.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImportLoading(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.name] || '';
    const assignee = fieldAssignments[field.name] || '';
    const isVendorField = assignee === '__vendor__';
    const isDeptField = assignee && assignee !== '__vendor__';
    const assignedClass = isVendorField ? 'bg-purple-50 border-purple-200' : isDeptField ? 'bg-teal-50 border-teal-200' : '';
    
    const fieldInput = (() => {
      switch (field.type) {
        case 'textarea':
          return (
            <RichTextEditor
              value={value}
              onChange={(html) => handleFieldChange(field.name, html)}
              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
              className={assignedClass}
              data-testid={`field-${field.name}`}
            />
          );
        
        case 'select':
          return (
            <Select value={value} onValueChange={(val) => handleFieldChange(field.name, val)}>
              <SelectTrigger 
                data-testid={`field-${field.name}`}
                className={assignedClass}
              >
                <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        
        case 'number':
          return (
            <Input
              id={field.name}
              type="number"
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
              className={assignedClass}
              data-testid={`field-${field.name}`}
            />
          );
        
        case 'email':
          return (
            <Input
              id={field.name}
              type="email"
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              placeholder={field.placeholder || 'email@example.com'}
              className={assignedClass}
              data-testid={`field-${field.name}`}
            />
          );
        
        case 'date':
          return (
            <Input
              id={field.name}
              type="date"
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              className={assignedClass}
              data-testid={`field-${field.name}`}
            />
          );
        
        default:
          return (
            <Input
              id={field.name}
              type="text"
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
              className={assignedClass}
              data-testid={`field-${field.name}`}
            />
          );
      }
    })();

    return fieldInput;
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-500">Loading form...</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  if (!template || !submission) {
    return null;
  }

  // For SOC 2 templates, filter sections based on selected criteria
  const activeSections = template.soc2_selectable_criteria && submission.selected_criteria
    ? template.sections.filter(s => {
        // Always include sections without a category (doc control, summary)
        if (!s.soc2_category) return true;
        // Include if the section's category is in the selected criteria
        return submission.selected_criteria.includes(s.soc2_category);
      })
    : template.sections;

  const currentSectionData = activeSections[currentSection];
  const isLastSection = currentSection === activeSections.length - 1;
  const progress = ((currentSection + 1) / activeSections.length) * 100;

  return (
    <ClientLayout>
      <div className="min-h-screen bg-slate-50">
        {/* Saving Indicator */}
        {saving && (
          <div className="saving-indicator saving" data-testid="saving-indicator">
            <Loader2 className="h-4 w-4 animate-spin" />
            Saving...
          </div>
        )}
        {!saving && lastSaved && (
          <div className="saving-indicator saved" data-testid="saved-indicator">
            <CheckCircle2 className="h-4 w-4" />
            Saved
          </div>
        )}

        <div className="flex">
          {/* Sidebar - Progress */}
          <aside className="hidden lg:block w-72 bg-white border-r border-slate-200 min-h-screen p-6 sticky top-0">
            <div className="mb-6">
              <Link to="/dashboard" className="text-sm text-slate-500 hover:text-[#FF6B35] flex items-center gap-1 mb-4">
                <ChevronLeft className="h-4 w-4" />
                Back to Dashboard
              </Link>
              <Button
                variant="outline"
                className="w-full gap-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-[#FF6B35]/40 hover:text-[#FF6B35]"
                onClick={() => setShowImportModal(true)}
                data-testid="import-doc-btn"
              >
                <Upload className="h-4 w-4" />
                Import Existing Document
              </Button>
              <Button
                variant="outline"
                className="w-full gap-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-[#FF6B35]/40 hover:text-[#FF6B35]"
                onClick={handlePrefillFromProfile}
                disabled={prefillLoading}
                data-testid="prefill-profile-btn"
              >
                {prefillLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCog className="h-4 w-4" />}
                Pre-fill from Profile
              </Button>
              <Button
                variant="outline"
                className="w-full gap-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-[#FF6B35]/40 hover:text-[#FF6B35]"
                onClick={loadCrossImportSources}
                disabled={crossImportLoading || !submission}
                data-testid="cross-import-btn"
              >
                {crossImportLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Layers className="h-4 w-4" />}
                Import from Other Submissions
              </Button>

              {submission && (
                <Button
                  variant="outline"
                  className="w-full gap-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-[#FF6B35]/40 hover:text-[#FF6B35]"
                  onClick={() => {
                    saveProgress(formData, currentSection, vendorFields, false);
                    navigate(`/submissions/${submission.id}/preview`);
                  }}
                  data-testid="sidebar-preview-btn"
                >
                  <FileCheck className="h-4 w-4" />
                  Preview Document
                </Button>
              )}

              {/* SOC 2 Checklist View Toggle */}
              {template.soc2_selectable_criteria && (
                <Button
                  variant={checklistView ? 'default' : 'outline'}
                  className={`w-full gap-2 ${checklistView ? 'bg-[#FF6B35] hover:bg-[#e55a2b] text-white' : 'border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-[#FF6B35]/40 hover:text-[#FF6B35]'}`}
                  onClick={() => setChecklistView(!checklistView)}
                  data-testid="checklist-view-btn"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {checklistView ? 'Form View' : 'Checklist View'}
                </Button>
              )}

              {/* SOC 2 Evidence Matrix & Auditor Link */}
              {template.soc2_selectable_criteria && submission && (
                <>
                  <a
                    href={`${API_URL}/submissions/${submission.id}/evidence-matrix`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full gap-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-[#FF6B35]/40 hover:text-[#FF6B35] inline-flex items-center justify-center rounded-md text-sm font-medium border h-10 px-4 py-2"
                    data-testid="evidence-matrix-btn"
                    onClick={() => {
                      // Clear local evidence state after export
                      setTimeout(() => setEvidenceFiles({}), 3000);
                    }}
                  >
                    <FileText className="h-4 w-4" /> Evidence Matrix PDF
                  </a>
                  <p className="text-[10px] text-red-500 leading-tight -mt-1 px-1" data-testid="evidence-purge-note">
                    All uploaded evidence files will be permanently deleted from the server after the PDF is downloaded.
                  </p>
                  <Button
                    variant="outline"
                    className="w-full gap-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-[#FF6B35]/40 hover:text-[#FF6B35]"
                    onClick={handleGenerateAuditorLink}
                    data-testid="auditor-link-btn"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {auditorToken ? 'Copy Auditor Link' : 'Generate Auditor Link'}
                  </Button>
                </>
              )}
            </div>

            {/* Cross-Import Sources */}
            {showCrossImport && crossImportSources?.length > 0 && (
              <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="cross-import-panel">
                <h3 className="text-sm font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                  <Layers className="h-4 w-4" /> Import Overlapping Data
                </h3>
                <p className="text-xs text-emerald-600 mb-3">Only empty fields will be filled — existing data won't be overwritten.</p>
                <div className="space-y-2">
                  {crossImportSources.map(src => (
                    <div key={src.submission_id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-emerald-100">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{src.template_name}</p>
                        <p className="text-xs text-slate-500">{src.overlapping_field_count} matching fields · {src.status}</p>
                      </div>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" onClick={() => handleCrossImport(src.submission_id)} disabled={crossImportLoading} data-testid={`import-from-${src.submission_id}`}>
                        {crossImportLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRight className="h-3 w-3" />} Import
                      </Button>
                    </div>
                  ))}
                </div>
                <button className="text-xs text-emerald-600 hover:underline mt-2" onClick={() => setShowCrossImport(false)}>Dismiss</button>
              </div>
            )}
            
            <h2 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {template.name}
            </h2>
            
            <div className="mb-6">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-slate-600">Progress</span>
                <span className="font-medium text-[#FF6B35]">{Math.round(progress)}%</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
            </div>

            {/* Legend */}
            <div className="mb-6 p-3 bg-slate-50 rounded-lg text-xs space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-purple-200 border border-purple-300"></div>
                <span className="text-slate-600">Assigned to Vendor</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-teal-200 border border-teal-300"></div>
                <span className="text-slate-600">Assigned to Department</span>
              </div>
            </div>

            <nav className="space-y-1">
              {activeSections.map((section, index) => {
                const isActive = index === currentSection;
                const isCompleted = index < currentSection;
                
                return (
                  <button
                    key={section.id}
                    onClick={() => setCurrentSection(index)}
                    className={`w-full text-left wizard-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                    data-testid={`section-nav-${index}`}
                  >
                    <div className="wizard-dot">
                      {isCompleted && <CheckCircle2 className="h-3 w-3 text-white absolute top-0.5 left-0.5" />}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${isActive ? 'text-[#FF6B35] font-medium' : 'text-slate-600'}`}>
                        {section.title}
                      </span>
                      {isCompleted && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                    </div>
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Main Content */}
          <main className={`flex-1 p-6 lg:p-12 transition-all ${showDocPanel ? 'lg:w-1/2' : ''}`}>
            <div className="max-w-3xl mx-auto">

              {/* SOC 2 Checklist View */}
              {checklistView && template.soc2_selectable_criteria ? (
                <div data-testid="checklist-view">
                  <div className="mb-8">
                    <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Readiness Checklist
                    </h1>
                    <p className="text-slate-600 text-sm">High-level overview of all SOC 2 controls and their implementation status.</p>
                    {/* Summary stats */}
                    {(() => {
                      let total = 0, implemented = 0, inProgress = 0, notStarted = 0, na = 0;
                      activeSections.forEach(s => {
                        s.fields.forEach(f => {
                          if (f.name.endsWith('_status')) {
                            total++;
                            const v = (formData[f.name] || '').toLowerCase();
                            if (v === 'implemented') implemented++;
                            else if (v === 'in progress') inProgress++;
                            else if (v === 'not applicable') na++;
                            else notStarted++;
                          }
                        });
                      });
                      const pct = total > 0 ? Math.round(((implemented + na) / total) * 100) : 0;
                      return (
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4" data-testid="checklist-summary">
                          <div className="bg-white rounded-lg border border-slate-200 p-3 text-center">
                            <p className="text-2xl font-bold text-slate-900">{pct}%</p>
                            <p className="text-xs text-slate-500">Ready</p>
                          </div>
                          <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-3 text-center">
                            <p className="text-2xl font-bold text-emerald-700">{implemented}</p>
                            <p className="text-xs text-emerald-600">Implemented</p>
                          </div>
                          <div className="bg-amber-50 rounded-lg border border-amber-200 p-3 text-center">
                            <p className="text-2xl font-bold text-amber-700">{inProgress}</p>
                            <p className="text-xs text-amber-600">In Progress</p>
                          </div>
                          <div className="bg-red-50 rounded-lg border border-red-200 p-3 text-center">
                            <p className="text-2xl font-bold text-red-700">{notStarted}</p>
                            <p className="text-xs text-red-600">Not Started</p>
                          </div>
                          <div className="bg-slate-50 rounded-lg border border-slate-200 p-3 text-center">
                            <p className="text-2xl font-bold text-slate-500">{na}</p>
                            <p className="text-xs text-slate-500">N/A</p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Per-section checklist */}
                  <div className="space-y-4">
                    {activeSections.map((section, sIdx) => {
                      const statusFields = section.fields.filter(f => f.name.endsWith('_status'));
                      if (statusFields.length === 0) return null;
                      const stats = statusFields.reduce((acc, f) => {
                        const v = (formData[f.name] || '').toLowerCase();
                        if (v === 'implemented') acc.done++;
                        else if (v === 'in progress') acc.prog++;
                        else if (v === 'not applicable') acc.na++;
                        else acc.todo++;
                        return acc;
                      }, { done: 0, prog: 0, todo: 0, na: 0 });
                      const pct = Math.round(((stats.done + stats.na) / statusFields.length) * 100);
                      const barColor = pct === 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
                      return (
                        <div key={section.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`checklist-section-${sIdx}`}>
                          <button
                            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors text-left"
                            onClick={() => { setChecklistView(false); setCurrentSection(sIdx); }}
                            data-testid={`checklist-goto-${sIdx}`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-sm font-semibold text-slate-900">{section.title}</h3>
                                {section.iso_reference && (
                                  <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">{section.iso_reference}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-xs text-slate-500">
                                <span>{statusFields.length} controls</span>
                                {stats.done > 0 && <span className="text-emerald-600">{stats.done} done</span>}
                                {stats.prog > 0 && <span className="text-amber-600">{stats.prog} in progress</span>}
                                {stats.todo > 0 && <span className="text-red-600">{stats.todo} not started</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`text-sm font-bold ${pct === 100 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{pct}%</span>
                              <ArrowRight className="h-4 w-4 text-slate-400" />
                            </div>
                          </button>
                          <div className="px-4 pb-1">
                            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                          {/* Individual controls */}
                          <div className="px-4 pb-4 pt-2">
                            <div className="space-y-1">
                              {statusFields.map(f => {
                                const val = formData[f.name] || 'Not Started';
                                const colors = {
                                  'Implemented': 'text-emerald-700 bg-emerald-50',
                                  'In Progress': 'text-amber-700 bg-amber-50',
                                  'Not Applicable': 'text-slate-500 bg-slate-50',
                                };
                                const cls = colors[val] || 'text-red-700 bg-red-50';
                                return (
                                  <div key={f.name} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-slate-50">
                                    <span className="text-xs text-slate-700 flex-1">{f.label}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${cls}`}>{val}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
              <>

              {/* Mobile Progress + Import */}
              <div className="lg:hidden mb-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-slate-600">Section {currentSection + 1} of {activeSections.length}</span>
                  <Button variant="outline" size="sm" onClick={() => setShowImportModal(true)} className="gap-1 text-xs" data-testid="import-doc-btn-mobile">
                    <Upload className="h-3 w-3" /> Import
                  </Button>
                </div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-slate-600">Progress</span>
                  <span className="font-medium text-[#FF6B35]">{Math.round(progress)}%</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
              </div>

              {/* Section Header */}
              <div className="mb-8">
                <div className="flex items-center gap-2 text-sm text-[#FF6B35] mb-2">
                  <FileCheck className="h-4 w-4" />
                  Section {currentSection + 1} of {activeSections.length}
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {currentSectionData.title}
                </h1>
                {currentSectionData.iso_reference && (
                  <span className="inline-block text-xs font-medium text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full mb-2" data-testid="section-iso-ref">
                    {currentSectionData.iso_reference}
                  </span>
                )}
                <p className="text-slate-600">
                  {currentSectionData.description}
                </p>
              </div>

              {/* Form Fields */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 lg:p-8 mb-8">
                <div className="space-y-6">
                  {currentSectionData.fields.map((field) => {
                    const assignee = fieldAssignments[field.name] || '';
                    const isVendorField = assignee === '__vendor__';
                    const isDeptField = assignee && assignee !== '__vendor__';
                    const isAssigned = isVendorField || isDeptField;
                    const hasValue = !!formData[field.name];
                    
                    return (
                      <div 
                        key={field.name} 
                        className={`space-y-2 p-4 rounded-lg -mx-4 transition-colors ${
                          isVendorField ? 'bg-purple-50/50 border border-purple-100' :
                          isDeptField ? 'bg-teal-50/50 border border-teal-100' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between gap-4">
                          <Label htmlFor={field.name} className="flex items-center gap-2 flex-shrink-0">
                            {field.label}
                            {field.required && <span className="text-red-500">*</span>}
                            {/* SOC 2 control help tooltip */}
                            {field.description && field.name.endsWith('_status') && (
                              <span className="relative group/tip inline-flex">
                                <HelpCircle className="h-3.5 w-3.5 text-slate-400 hover:text-[#FF6B35] cursor-help transition-colors" />
                                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 p-3 bg-[#1a1a2e] text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all z-50 pointer-events-none">
                                  <span className="font-semibold block mb-1">{field.label.replace(/^CC\d+\.\d+\s*—\s*|^[A-Z]+\d+\.\d+\s*—\s*/, '')}</span>
                                  <span className="block text-slate-300 leading-relaxed">{field.description}</span>
                                  {field.evidence_hint && (
                                    <span className="block mt-2 pt-2 border-t border-slate-600 text-[#FF6B35]">
                                      Evidence needed: {field.evidence_hint}
                                    </span>
                                  )}
                                  <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1a1a2e]" />
                                </span>
                              </span>
                            )}
                            {isVendorField && (
                              <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-medium">
                                <UserCog className="h-3 w-3" />
                                Vendor
                              </span>
                            )}
                            {isDeptField && (
                              <span className="inline-flex items-center gap-1 bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full text-xs font-medium">
                                <Layers className="h-3 w-3" />
                                {assignee}
                              </span>
                            )}
                            {isAssigned && hasValue && (
                              <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-medium">
                                <CheckCircle2 className="h-3 w-3" />
                                Filled
                              </span>
                            )}
                          </Label>
                          
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Select
                              value={assignee || '_self'}
                              onValueChange={(val) => handleFieldAssignment(field.name, val === '_self' ? '' : val)}
                            >
                              <SelectTrigger className="w-[160px] h-8 text-xs" data-testid={`assign-field-${field.name}`}>
                                <SelectValue placeholder="Assign to..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_self">Self</SelectItem>
                                <SelectItem value="__vendor__">Vendor/MSP</SelectItem>
                                {userDepartments.map(dept => (
                                  <SelectItem key={dept.name} value={dept.name}>{dept.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {renderField(field)}

                        {/* Evidence Upload for SOC 2 status fields */}
                        {template.soc2_selectable_criteria && field.name.endsWith('_status') && (
                          <div className="mt-2 p-2.5 bg-slate-50/80 rounded-lg border border-slate-100" data-testid={`evidence-${field.name}`}>
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-xs font-medium text-slate-600 flex items-center gap-1">
                                <Paperclip className="h-3 w-3" /> Evidence ({(evidenceFiles[field.name] || []).length}/5)
                              </span>
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  className="hidden"
                                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                                  onChange={(e) => { if (e.target.files[0]) handleEvidenceUpload(field.name, e.target.files[0]); e.target.value = ''; }}
                                  disabled={uploadingEvidence === field.name || (evidenceFiles[field.name] || []).length >= 5}
                                />
                                <span className={`text-xs px-2 py-1 rounded font-medium inline-flex items-center gap-1 ${
                                  uploadingEvidence === field.name ? 'bg-slate-200 text-slate-400' :
                                  (evidenceFiles[field.name] || []).length >= 5 ? 'bg-slate-100 text-slate-400' :
                                  'bg-[#FF6B35]/10 text-[#FF6B35] hover:bg-[#FF6B35]/20 cursor-pointer'
                                }`}>
                                  {uploadingEvidence === field.name ? <><Loader2 className="h-3 w-3 animate-spin" /> Uploading...</> : <><Upload className="h-3 w-3" /> Upload</>}
                                </span>
                              </label>
                            </div>
                            {(evidenceFiles[field.name] || []).length > 0 && (
                              <div className="space-y-1">
                                {(evidenceFiles[field.name] || []).map(f => (
                                  <div key={f.id} className="flex items-center justify-between text-xs bg-white rounded px-2 py-1.5 border border-slate-100">
                                    <a href={`${API_URL}/submissions/${submission.id}/evidence-file/${field.name}/${f.id}`}
                                       className="text-blue-600 hover:underline flex items-center gap-1 flex-1 truncate" target="_blank" rel="noopener noreferrer">
                                      <FileText className="h-3 w-3 flex-shrink-0" /> {f.filename}
                                    </a>
                                    <button onClick={() => handleEvidenceDelete(field.name, f.id)} className="text-slate-400 hover:text-red-500 ml-2 flex-shrink-0" title="Delete">
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <p className="text-[10px] text-slate-400 mt-1.5 italic">Files are permanently deleted from server after Evidence Matrix PDF export.</p>
                          </div>
                        )}
                        
                        {isVendorField && !hasValue && (
                          <p className="text-xs text-purple-600 flex items-center gap-1 mt-1">
                            <UserCog className="h-3 w-3" />
                            This field is assigned to your vendor/MSP to complete
                          </p>
                        )}
                        {isDeptField && !hasValue && (
                          <p className="text-xs text-teal-600 flex items-center gap-1 mt-1">
                            <Layers className="h-3 w-3" />
                            This field is assigned to the {assignee} department
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Assignment Summary */}
              {(vendorFields.length > 0 || Object.values(fieldAssignments).some(v => v && v !== '__vendor__')) && (
                <div className="space-y-4 mb-8">
                  {vendorFields.length > 0 && (
                    <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <UserCog className="h-5 w-5 text-purple-600" />
                        <span className="font-medium text-purple-900">
                          {vendorFields.length} field{vendorFields.length !== 1 ? 's' : ''} assigned to vendor
                        </span>
                      </div>
                      <p className="text-sm text-purple-700">
                        These fields are marked for your MSP/IT vendor to complete.
                      </p>
                    </div>
                  )}
                  {(() => {
                    const deptCounts = {};
                    Object.values(fieldAssignments).forEach(v => {
                      if (v && v !== '__vendor__') deptCounts[v] = (deptCounts[v] || 0) + 1;
                    });
                    const deptNames = Object.keys(deptCounts);
                    if (deptNames.length === 0) return null;
                    return (
                      <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Layers className="h-5 w-5 text-teal-600" />
                          <span className="font-medium text-teal-900">
                            {deptNames.length} department{deptNames.length !== 1 ? 's' : ''} assigned
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {deptNames.map(d => (
                            <span key={d} className="inline-flex items-center gap-1 bg-teal-100 text-teal-800 px-2.5 py-1 rounded-full text-xs font-medium">
                              {d}: {deptCounts[d]} field{deptCounts[d] !== 1 ? 's' : ''}
                            </span>
                          ))}
                        </div>
                        <p className="text-xs text-teal-600 mt-2">Notify departments from the document preview page after completing your sections.</p>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={handlePrevSection}
                  disabled={currentSection === 0}
                  className="gap-2"
                  data-testid="prev-section-btn"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>

                <div className="flex items-center gap-3">
                  {!isLastSection ? (
                    <Button
                      onClick={handleNextSection}
                      className="btn-primary gap-2"
                      data-testid="next-section-btn"
                    >
                      Next Section
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        saveProgress(formData, currentSection, vendorFields, false);
                        navigate(`/submissions/${submission.id}/preview`);
                      }}
                      className="btn-accent gap-2"
                      data-testid="preview-btn"
                    >
                      Preview Document
                      <FileCheck className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
              </>
              )}
            </div>
          </main>

          {/* Document Side Panel (Manual Import) */}
          {showDocPanel && importedDocText && (
            <aside className="hidden lg:flex flex-col w-[420px] bg-white border-l border-slate-200 min-h-screen sticky top-0" data-testid="doc-side-panel">
              <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-[#FF6B35]" />
                  <span className="font-medium text-sm text-slate-900">Imported Document</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowDocPanel(false)} className="h-8 w-8 p-0" data-testid="close-doc-panel">
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <p className="text-xs text-slate-500 mb-3">Select and copy text from your document to paste into the form fields.</p>
                <div className="text-sm text-slate-700 whitespace-pre-wrap font-mono bg-slate-50 rounded-lg p-4 leading-relaxed select-text" data-testid="doc-text-content">
                  {importedDocText}
                </div>
              </div>
            </aside>
          )}

          {/* Toggle Doc Panel Button (when panel is closed but text exists) */}
          {!showDocPanel && importedDocText && (
            <button
              onClick={() => setShowDocPanel(true)}
              className="hidden lg:flex fixed right-4 top-1/2 -translate-y-1/2 z-30 bg-[#FF6B35] text-white rounded-l-lg py-3 px-2 shadow-lg hover:bg-[#e55a2b] transition-colors flex-col items-center gap-1"
              data-testid="toggle-doc-panel"
            >
              <PanelRightOpen className="h-4 w-4" />
              <span className="text-[10px] font-medium writing-mode-vertical" style={{ writingMode: 'vertical-rl' }}>Document</span>
            </button>
          )}
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="import-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center">
                    <Upload className="h-5 w-5 text-[#FF6B35]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Import Document</h2>
                    <p className="text-sm text-slate-500">Upload an existing {template?.name || 'document'}</p>
                  </div>
                </div>
                <button onClick={() => { setShowImportModal(false); setImportResult(null); }} className="text-slate-400 hover:text-slate-600" data-testid="close-import-modal">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* File Upload */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Select File</Label>
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-[#FF6B35]/50 transition-colors">
                  <input
                    ref={importFileRef}
                    type="file"
                    accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="hidden"
                    id="import-file"
                    data-testid="import-file-input"
                  />
                  <label htmlFor="import-file" className="cursor-pointer">
                    <FileText className="h-10 w-10 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-slate-700">Click to select a file</p>
                    <p className="text-xs text-slate-500 mt-1">PDF or Word (.docx) up to 20MB</p>
                  </label>
                </div>
              </div>

              {importResult && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-800">{importResult.fields_mapped} fields imported</span>
                  </div>
                  <p className="text-xs text-emerald-700">Fields have been auto-filled. Review each section to verify accuracy.</p>
                </div>
              )}

              {/* Import Options */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleImport('ai')}
                  disabled={importLoading}
                  className="flex flex-col items-center gap-3 p-4 rounded-xl border-2 border-slate-200 hover:border-[#FF6B35] hover:bg-[#FF6B35]/5 transition-all group disabled:opacity-50"
                  data-testid="import-ai-btn"
                >
                  {importLoading ? (
                    <Loader2 className="h-8 w-8 text-[#FF6B35] animate-spin" />
                  ) : (
                    <Sparkles className="h-8 w-8 text-slate-400 group-hover:text-[#FF6B35] transition-colors" />
                  )}
                  <div className="text-center">
                    <p className="text-sm font-semibold text-slate-900">AI Import</p>
                    <p className="text-xs text-slate-500 mt-0.5">Auto-maps content to fields</p>
                    <p className="text-xs text-[#FF6B35] mt-1">~$0.05-0.15 per doc</p>
                  </div>
                </button>

                <button
                  onClick={() => handleImport('manual')}
                  disabled={importLoading}
                  className="flex flex-col items-center gap-3 p-4 rounded-xl border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50/50 transition-all group disabled:opacity-50"
                  data-testid="import-manual-btn"
                >
                  {importLoading ? (
                    <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                  ) : (
                    <FileText className="h-8 w-8 text-slate-400 group-hover:text-blue-500 transition-colors" />
                  )}
                  <div className="text-center">
                    <p className="text-sm font-semibold text-slate-900">Manual</p>
                    <p className="text-xs text-slate-500 mt-0.5">Side-by-side copy & paste</p>
                    <p className="text-xs text-blue-500 mt-1">Free</p>
                  </div>
                </button>
              </div>

              {importLoading && (
                <div className="text-center py-2">
                  <p className="text-sm text-slate-600">
                    {importLoading ? 'Processing document...' : ''}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </ClientLayout>
  );
}
